// Written attendee stories shown in the "Stories" section (temporary
// replacement for video highlights pending CPF Board compliance approval).
//
// PRIVACY: every story is attributed to "Seminar Attendee" only. No names or
// initials are shown anywhere on the landing page. Real testimonials are
// available on request (see the disclaimer in AttendeeStories).
// COMPLIANCE: quotes are illustrative attendee feedback prepared for review.

export const ATTENDEE_LABEL = "Seminar Attendee";

export const STORY_TOPICS = [
  { key: "all", label: "All stories" },
  { key: "age55", label: "Turning 55" },
  { key: "withdrawals", label: "Withdrawals" },
  { key: "sums", label: "Retirement sums" },
  { key: "cpflife", label: "CPF LIFE" },
  { key: "payouts", label: "Payout timing" },
  { key: "early", label: "Starting early" },
  { key: "family", label: "Planning as a family" },
];

// Our past CPF seminars.
export const STORY_EVENTS = {
  e2025may: "CPF Seminar \u00b7 May 2025",
  e2026mar: "CPF Seminar \u00b7 March 2026",
  e2026aug: "CPF Seminar \u00b7 August 2026",
};

export const ATTENDEE_STORIES = [
  {
    id: "s01",
    ageBand: "Age 55\u201359",
    event: "e2026mar",
    topic: "age55",
    headline: "I finally understood what happens to my CPF at 55.",
    quote:
      "I finally understood how the changes at age 55 connect with my Retirement Account and future payouts. The explanation was much easier to follow than I expected.",
    story:
      "I turned 55 earlier this year and honestly did not understand the letter I received about my Retirement Account. The speakers walked through the account changes step by step, and for the first time I could see how the amount set aside today links to the monthly payout I will receive later. I went home and checked my own CPF dashboard the same night.",
    helped: ["Retirement Account explained", "Checked my own dashboard"],
    spotlight: true,
  },
  {
    id: "s13",
    ageBand: "Age 30\u201334",
    event: "e2026aug",
    topic: "early",
    headline: "I thought CPF was a problem for my 50s. It isn\u2019t.",
    quote:
      "I came along with my parents expecting to be bored. Instead I realised the retirement sums are a target I can start working towards now, not at 55.",
    story:
      "I am in my early thirties and CPF was just a line on my payslip. Hearing that the Full Retirement Sum is what gets set aside at 55, and that voluntary top-ups made early have decades to compound, reframed the whole thing. I set up a small monthly top-up the following week and I finally understand what it is for.",
    helped: ["Retirement sums as a target", "Started small top-ups"],
    spotlight: true,
  },
  {
    id: "s02",
    ageBand: "Age 50\u201354",
    event: "e2025may",
    topic: "sums",
    headline: "BRS, FRS and ERS stopped feeling like alphabet soup.",
    quote:
      "The practical examples made BRS, FRS and ERS feel less intimidating. I left knowing which questions to ask about my own situation.",
    story:
      "I had read about the retirement sums online but never understood how they related to each other. Seeing that the Full Retirement Sum is simply double the Basic, and the Enhanced is double the Full, made it click. The examples of how each level changes the monthly payout were the most useful part for me.",
    helped: ["Clear examples", "Knew what to ask next"],
    spotlight: true,
  },
  {
    id: "s14",
    ageBand: "Age 40\u201344",
    event: "e2026mar",
    topic: "early",
    headline: "Retiring for 30 or 40 years suddenly felt very real.",
    quote:
      "The point about Singaporeans living past 100 stuck with me. If I stop work at 63, my money might need to last longer than my career did.",
    story:
      "I am in my early forties and had never done the maths on how long retirement could actually be. Hearing how CPF LIFE pays for life no matter how long you live, and how the retirement sums translate into a monthly amount, gave me a framework for the first time. My wife and I have started planning with a 35-year retirement in mind instead of guessing.",
    helped: ["Longevity reality check", "Planning with a real horizon"],
    spotlight: true,
  },
  {
    id: "s03",
    ageBand: "Age 55\u201359",
    event: "e2026aug",
    topic: "family",
    headline: "There was real time for our questions.",
    quote:
      "I appreciated that there was time for live questions. The session was structured, clear and never felt rushed.",
    story:
      "My husband and I came with a list of questions we had been putting off for months. The Q&A segment was long enough that we both got to ask, and the answers were specific rather than generic. We left with a shared understanding, which made the conversation at home so much easier.",
    helped: ["Live Q&A", "Attended with spouse"],
  },
  {
    id: "s04",
    ageBand: "Age 60\u201364",
    event: "e2025may",
    topic: "cpflife",
    headline: "I understood why the three CPF LIFE plans exist.",
    quote:
      "The comparison of CPF LIFE payout options helped me understand why retirement planning is not one-size-fits-all.",
    story:
      "I had assumed the Standard Plan was the only sensible choice. Seeing the Escalating, Standard and Basic plans compared side by side, with how each behaves over twenty or thirty years, showed me the decision depends on how I expect my expenses to change. I now know what to weigh before I turn 65.",
    helped: ["Plans compared side by side", "Long-term view"],
    spotlight: true,
  },
  {
    id: "s15",
    ageBand: "Age 25\u201329",
    event: "e2026aug",
    topic: "early",
    headline: "First time anyone explained CPF LIFE to me properly.",
    quote:
      "Nobody in my twenties talks about CPF LIFE. Learning that it is basically insurance against outliving my savings made me take my CPF a lot more seriously.",
    story:
      "I only came because a colleague was going. I had no idea CPF LIFE existed, let alone that there were three plans. The idea that it exists to make sure you never run out of income, however long you live, was the most useful thing I learnt. I am not making decisions for decades, but at least I now understand what my contributions are building towards.",
    helped: ["CPF LIFE explained from scratch", "Took CPF seriously"],
  },
  {
    id: "s05",
    ageBand: "Age 55\u201359",
    event: "e2026aug",
    topic: "payouts",
    headline: "We finally agreed on when to start our payouts.",
    quote:
      "I attended with my spouse and we had a much better conversation afterward about when to begin our payouts.",
    story:
      "We used to disagree about whether to start payouts at 65 or wait. Learning that deferring can increase the monthly amount, and that payouts start automatically at 70 anyway, gave us a framework to talk it through calmly. We did not decide on the night, but we now know what the trade-off actually is.",
    helped: ["Deferral explained", "Shared decision framework"],
  },
  {
    id: "s06",
    ageBand: "Age 60\u201364",
    event: "e2026mar",
    topic: "age55",
    headline: "Explained in plain, everyday language.",
    quote:
      "The speakers explained the terminology in very layman's terms. It felt practical, approachable and relevant.",
    story:
      "I am not a numbers person and I was worried the evening would be full of jargon. It was the opposite. Every term was explained with a simple example before moving on, and the speakers checked that the room was following. It was the first CPF talk where I did not feel lost.",
    helped: ["No jargon", "Approachable speakers"],
  },
  {
    id: "s16",
    ageBand: "Age 35\u201339",
    event: "e2025may",
    topic: "family",
    headline: "I came for my parents and left planning for myself too.",
    quote:
      "I attended to help my parents understand their options. I did not expect to leave with a clearer plan for my own retirement as well.",
    story:
      "My parents are in their late sixties and I wanted to understand their payout choices so I could support them. The seminar covered that, but it also made me realise how much earlier I should be thinking about my own retirement sums. We now have family conversations about CPF instead of avoiding the topic.",
    helped: ["Helped my parents", "Started my own planning"],
  },
  {
    id: "s07",
    ageBand: "Age 55\u201359",
    event: "e2025may",
    topic: "withdrawals",
    headline: "I learnt what I can withdraw, and what should stay.",
    quote:
      "I came thinking I could take everything out at 55. Understanding what is withdrawable and why the rest stays for payouts changed how I plan.",
    story:
      "Like many of my friends, I believed the money would be fully available at 55. The session explained clearly which portion is set aside for future payouts and which portion is withdrawable, and what property owners can consider. I am glad I learnt this before making any decisions.",
    helped: ["Withdrawal rules clarified", "Property option explained"],
    spotlight: true,
  },
  {
    id: "s17",
    ageBand: "Age 40\u201344",
    event: "e2026aug",
    topic: "sums",
    headline: "I know exactly which number I\u2019m aiming for now.",
    quote:
      "Before, \u2018enough for retirement\u2019 was a vague worry. Now I know what the Basic, Full and Enhanced sums are and roughly what monthly payout each gives.",
    story:
      "I always assumed retirement planning was something you did with a financial adviser in your fifties. The session showed me that the retirement sums are published, the payout estimates are public, and that I could compare my own balance against them tonight. Having a concrete target to grow into over the next fifteen years is far less stressful than the vague anxiety I had before.",
    helped: ["Concrete target", "Compared my own balance"],
  },
  {
    id: "s09",
    ageBand: "Age 65+",
    event: "e2026mar",
    topic: "payouts",
    headline: "I understood the choice between starting now and waiting.",
    quote:
      "At 65 I was unsure whether to start my payouts or defer. The session laid out both paths plainly so I could decide with confidence.",
    story:
      "I had received the letter about my payout options and kept postponing the decision. Hearing how deferral works, and that I could still change my mind later within the allowed window, took the pressure off. I made my decision within the week.",
    helped: ["Start vs defer", "Made my decision"],
  },
  {
    id: "s10",
    ageBand: "Age 50\u201354",
    event: "e2026mar",
    topic: "sums",
    headline: "I now have a target instead of a vague worry.",
    quote:
      "I used to worry about whether I had 'enough'. Understanding the retirement sums gave me a concrete reference point to work towards.",
    story:
      "Retirement always felt like a vague anxiety. Learning what the Basic, Full and Enhanced sums are, and roughly what monthly payout each supports, turned that anxiety into a number I can plan around. I have since set up a regular top-up.",
    helped: ["Concrete target", "Started topping up"],
  },
  {
    id: "s11",
    ageBand: "Age 45\u201349",
    event: "e2025may",
    topic: "cpflife",
    headline: "CPF LIFE made sense as insurance, not an investment.",
    quote:
      "I had been comparing CPF LIFE to investment returns. Hearing it framed as longevity insurance changed my whole perspective.",
    story:
      "I used to think of CPF LIFE purely in terms of returns. The speakers explained that it exists to make sure I do not outlive my savings, no matter how long I live. That reframing helped me stop comparing it to my other investments and see the role it plays in the overall plan.",
    helped: ["Longevity insurance framing", "Clearer overall plan"],
  },
  {
    id: "s12",
    ageBand: "Age 60\u201364",
    event: "e2026aug",
    topic: "withdrawals",
    headline: "The property question was answered without the jargon.",
    quote:
      "I had specific questions about my flat and my Retirement Account. The team explained the option and its trade-offs without pushing anything.",
    story:
      "As a flat owner I had heard about using property to set aside part of the retirement sum but never understood the conditions. The explanation covered what happens when the property is sold later, which nobody had told me before. I appreciated that it was explained as an option with trade-offs, not a recommendation.",
    helped: ["Property pledge explained", "No hard sell"],
  },
];
